import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactFlow, {
    addEdge, applyNodeChanges, applyEdgeChanges, Background, Controls,
    EdgeLabelRenderer, getSmoothStepPath, useReactFlow,
} from 'reactflow';
import { buildWaypointPath, snapToGrid, closestSegmentIndex, diameterToStrokeWidth } from './edge_path_utils';
import 'reactflow/dist/style.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../../AxiosInstance';
import MyContext from '../../Context';
import useMQTTMultiDevice from '../../components/mqtt_service/mqtt_multi_device';
import scadaNodeTypes from '../../nodes/scada/index';
import { isGateOpen } from '../../nodes/scada/signal_utils';
import { SIGNAL_CONFIG, PORT_CONFIG } from '../../nodes/scada/port_config';
import { initSimState, tickSimulationAll } from '../../nodes/scada/simulation_engine';
import SCADAElementPalette from './scada_element_palette';
import SCADAElementInspector from './scada_element_inspector';
import SCADAMachineTypes from './scada_machine_types';
import styles from './scada.module.css';
import Modal from '../../components/modal/modal';
import { TextInput } from '../../components/input/inputs';

let tempIdCounter = -1;
const newTempId = () => String(tempIdCounter--);

/** Map a source handle ID to the signal key that controls flow through it. */
function getEdgeSignalKey(nodeType, sourceHandle, config) {
    if (nodeType === 'ribbon_blender') {
        const gates = parseInt(config?.gate_count ?? 0);
        if (gates >= 2) {
            if (sourceHandle === 'out1') return 'gate_1';
            if (sourceHandle === 'out2') return 'gate_2';
        }
        if (gates === 1 && sourceHandle === 'out') return 'gate_1';
    }
    if (nodeType === 'cooler_blender' && sourceHandle === 'out') return 'outlet_state';
    if (nodeType === 'cooler_blender' && sourceHandle === 'air_out') return 'is_holding';
    if (nodeType === 'bucket_elevator') {
        if (sourceHandle === 'return') return 'outlet_return';
        const outlets = parseInt(config?.outlet_count ?? 1);
        if (outlets === 1 && sourceHandle === 'out') return 'outlet_1';
        const m = sourceHandle?.match(/^out(\d+)$/);
        if (m) return `outlet_${m[1]}`;
    }
    return 'state';
}

/** Returns true if the given source node is actively outputting through sourceHandle. */
function isEdgeHandleActive(sourceNode, sourceHandle) {
    if (!sourceNode) return false;
    const config     = sourceNode.data?.config || {};
    const liveValues = sourceNode.data?.liveValues || {};
    const threshold  = parseFloat(config.threshold ?? 0.5);

    // valve_3way: outa = active when state >= threshold; outb = active when state < threshold
    if (sourceNode.type === 'valve_3way') {
        const val = parseFloat(liveValues['state'] ?? NaN);
        if (isNaN(val)) return false;
        return sourceHandle === 'outb' ? val < threshold : val >= threshold;
    }

    const signalKey = getEdgeSignalKey(sourceNode.type, sourceHandle, config);

    // Gate-bearing handles: use isGateOpen for sensor_type + open_level logic
    const isGateHandle = (
        (sourceNode.type === 'ribbon_blender'  && ['out', 'out1', 'out2'].includes(sourceHandle) && (config.gate_count ?? 0) > 0) ||
        (sourceNode.type === 'cooler_blender'  && sourceHandle === 'out') ||
        (sourceNode.type === 'bucket_elevator' && /^out\d*$/.test(sourceHandle))
    );

    if (isGateHandle) {
        // cooler_blender backward compat: if outlet_state unbound, fall back to state
        const effectiveKey = (sourceNode.type === 'cooler_blender' && signalKey === 'outlet_state'
            && liveValues['outlet_state'] == null)
            ? 'state'
            : signalKey;

        if (effectiveKey === 'state') {
            const val = liveValues['state'];
            if (val == null) return false;
            return parseFloat(val) >= threshold;
        }

        let closeKey;
        if (sourceNode.type === 'ribbon_blender') {
            closeKey = effectiveKey === 'gate_1' ? 'gate_1_close' : 'gate_2_close';
        } else if (sourceNode.type === 'cooler_blender') {
            closeKey = 'outlet_state_close';
        } else {
            closeKey = effectiveKey + '_close'; // e.g. outlet_1 → outlet_1_close
        }

        const sensorType = (sourceNode.type === 'ribbon_blender')
            ? (config.gate_sensor_type   || 'single')
            : (config.outlet_sensor_type || 'single');
        const openLevel = (sourceNode.type === 'ribbon_blender')
            ? (config.gate_open_level   || 'high')
            : (config.outlet_open_level || 'high');

        return isGateOpen(liveValues, effectiveKey, closeKey, sensorType, openLevel, threshold) === true;
    }

    // Default: direct threshold check on signalKey
    const val = liveValues[signalKey];
    if (val == null) return false;
    return parseFloat(val) >= threshold;
}

function WaypointEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, data, animated }) {
    const reactFlow = useReactFlow();
    const { setEdges } = reactFlow;
    // v11.7 uses project() to convert screen → flow coords
    const toFlowPosition = (screenPos) => {
        const bounds = document.querySelector('.react-flow')?.getBoundingClientRect() || { left: 0, top: 0 };
        return reactFlow.project({ x: screenPos.x - bounds.left, y: screenPos.y - bounds.top });
    };
    const waypoints = data?.waypoints || [];
    const pipeStyle = data?.pipeStyle || false;
    const diameter = data?.diameter || 50;
    const flowColor = data?.flowColor || '#22C55E'; // default green, blue for chiller, red for boiler

    // Compute path
    let edgePath, labelX, labelY;
    if (waypoints.length === 0) {
        [edgePath, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
    } else {
        edgePath = buildWaypointPath(sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, waypoints);
        const mid = waypoints[Math.floor(waypoints.length / 2)];
        labelX = mid.x;
        labelY = mid.y - 20;
    }

    // Stroke width based on pipe style
    const pipeWidth = pipeStyle ? diameterToStrokeWidth(diameter) : 2;
    const flowWidth = pipeStyle ? Math.max(2, pipeWidth - 4) : 3;

    // Double-click to add waypoint
    const handleDoubleClick = (e) => {
        if (!data?.editMode) return;
        e.stopPropagation();
        const pos = toFlowPosition({ x: e.clientX, y: e.clientY });
        const snapped = { x: snapToGrid(pos.x), y: snapToGrid(pos.y) };
        const allPoints = [
            { x: sourceX, y: sourceY },
            ...waypoints,
            { x: targetX, y: targetY },
        ];
        const insertIdx = closestSegmentIndex(allPoints, snapped);
        setEdges(eds => eds.map(edge => {
            if (edge.id !== id) return edge;
            const wps = [...(edge.data?.waypoints || [])];
            wps.splice(insertIdx, 0, snapped);
            return { ...edge, data: { ...edge.data, waypoints: wps } };
        }));
    };

    // Drag a waypoint — use window-level listeners so the drag works even
    // when the pointer moves over other edges / nodes / the pane.
    const startDrag = (e, wpIdx) => {
        if (!data?.editMode) return;
        e.stopPropagation();
        e.preventDefault();

        const onMove = (me) => {
            me.preventDefault();
            const pos = toFlowPosition({ x: me.clientX, y: me.clientY });
            const snapped = { x: snapToGrid(pos.x), y: snapToGrid(pos.y) };
            setEdges(eds => eds.map(edge => {
                if (edge.id !== id) return edge;
                const wps = [...(edge.data?.waypoints || [])];
                wps[wpIdx] = snapped;
                return { ...edge, data: { ...edge.data, waypoints: wps } };
            }));
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove, true);
            window.removeEventListener('pointerup', onUp, true);
        };
        window.addEventListener('pointermove', onMove, true);
        window.addEventListener('pointerup', onUp, true);
    };

    // Remove waypoint on right-click
    const removeWaypoint = (e, wpIdx) => {
        e.preventDefault();
        e.stopPropagation();
        setEdges(eds => eds.map(edge => {
            if (edge.id !== id) return edge;
            const wps = [...(edge.data?.waypoints || [])];
            wps.splice(wpIdx, 1);
            return { ...edge, data: { ...edge.data, waypoints: wps } };
        }));
    };

    return (
        <>
            {/* Invisible fat hit-area for double-click */}
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={20}
                style={{ pointerEvents: 'stroke', cursor: data?.editMode ? 'crosshair' : 'default' }}
                onDoubleClick={handleDoubleClick}
            />
            {/* Pipe style: outer dark stroke */}
            {pipeStyle && (
                <path
                    d={edgePath}
                    fill="none"
                    className="react-flow__edge-path"
                    style={{ stroke: '#1E293B', strokeWidth: pipeWidth + 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}
                />
            )}
            {/* Main visible path */}
            {animated ? (
                <path
                    d={edgePath}
                    fill="none"
                    className="react-flow__edge-path"
                    style={{
                        stroke: flowColor,
                        strokeWidth: pipeStyle ? flowWidth : 3,
                        strokeDasharray: pipeStyle ? undefined : '10 5',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                    }}
                    markerEnd={markerEnd}
                >
                    {!pipeStyle && (
                        <animate attributeName="stroke-dashoffset" from="0" to="-15" dur="0.5s" repeatCount="indefinite" />
                    )}
                    {pipeStyle && (
                        <animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite" />
                    )}
                </path>
            ) : (
                <path
                    d={edgePath}
                    fill="none"
                    className="react-flow__edge-path"
                    style={{
                        stroke: pipeStyle ? (data?.pipeColor || '#475569') : (style?.stroke || '#94A3B8'),
                        strokeWidth: pipeStyle ? pipeWidth : (style?.strokeWidth || 2),
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                    }}
                    markerEnd={markerEnd}
                />
            )}
            {/* Edge label renderer: delete button + waypoint handles */}
            <EdgeLabelRenderer>
                {/* Delete button */}
                {data?.editMode && (
                    <div style={{ position: 'absolute', transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`, pointerEvents: 'all' }} className="nodrag nopan">
                        <button
                            onClick={() => setEdges(eds => eds.filter(e => e.id !== id))}
                            style={{ width: 18, height: 18, borderRadius: '50%', border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: 13, lineHeight: '18px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >×</button>
                    </div>
                )}
                {/* Draggable waypoint handles */}
                {data?.editMode && waypoints.map((wp, idx) => (
                    <div
                        key={idx}
                        className="nodrag nopan"
                        onPointerDown={(e) => startDrag(e, idx)}
                        onContextMenu={(e) => removeWaypoint(e, idx)}
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%,-50%) translate(${wp.x}px,${wp.y}px)`,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: '#4F46E5',
                            border: '2px solid #fff',
                            cursor: 'grab',
                            pointerEvents: 'all',
                            zIndex: 5,
                        }}
                        title="Drag to move · Right-click to remove"
                    />
                ))}
            </EdgeLabelRenderer>
        </>
    );
}

const scadaEdgeTypes = { deletable: WaypointEdge };

/**
 * Convert a SCADAElement from backend into a ReactFlow node.
 */
function elementToNode(el, isEditMode) {
    return {
        id: String(el.id),
        type: el.element_type,
        position: { x: el.pos_x, y: el.pos_y },
        data: {
            ...el,
            width: el.width,
            height: el.height,
            liveValue: null,
            isEditMode,
            variable_data_id: el.variable_data,
            variable_data_detail: el.variable_data_detail,
            machine_type_id: el.machine_type,
            machine_type_detail: el.machine_type_detail,
            config: el.config || {},
            label: el.label || '',
        },
        style: { width: el.width, height: el.height },
        draggable: isEditMode,
        selectable: isEditMode,
        connectable: isEditMode,
    };
}

/**
 * Convert a ReactFlow node back into a payload object for bulk_save.
 */
function nodeToPayload(node, scadaViewId) {
    const d = node.data;
    return {
        id: node.id,
        scada_view: scadaViewId,
        element_type: node.type,
        pos_x: Math.round(node.position.x),
        pos_y: Math.round(node.position.y),
        width: Math.round(node.style?.width || d.width || 80),
        height: Math.round(node.style?.height || d.height || 80),
        rotation: d.rotation || 0,
        z_index: d.z_index || 0,
        variable_data: d.variable_data_id || null,
        machine_type: d.machine_type_id || null,
        config: d.config || {},
        label: d.label || '',
    };
}

export default function SCADAEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setCompanyChange } = useContext(MyContext);
    const project_id = parseInt(localStorage.getItem('project_id'));

    const reactFlowWrapper = useRef(null);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);

    const [view, setView] = useState(null);          // current SCADAView metadata
    const [allViews, setAllViews] = useState([]);     // all screens for this project
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [deletedIds, setDeletedIds] = useState([]);
    const [showMachineTypes, setShowMachineTypes] = useState(false);
    const [showNewScreen, setShowNewScreen] = useState(false);
    const [newScreenForm, setNewScreenForm] = useState({ name: '' });
    const [isSimulationMode, setIsSimulationMode] = useState(false);
    const [simSpeedup, setSimSpeedup] = useState(60);
    const [simElapsed, setSimElapsed] = useState(0);   // simulated seconds elapsed
    const [actuatorPanel, setActuatorPanel] = useState(null); // { type: 'valve_3way'|'valve_ball', node }
    const [gatePanel, setGatePanel] = useState(null);  // { node } — gate control for blenders
    const [ballSliderValue, setBallSliderValue] = useState(50);
    const [hoveredNode, setHoveredNode] = useState(null); // node being hovered in view mode
    const simStateRef = useRef({});
    const edgesRef = useRef([]);
    const nodesRef = useRef([]);
    const simSpeedupRef = useRef(60);
    const hoverLeaveTimerRef = useRef(null);
    const pendingActuatorsRef = useRef({}); // { [nodeId]: { expectedState, timeoutId, threshold } }

    // Derive unique device IDs from ALL signal bindings across all nodes
    const deviceIds = useMemo(() => {
        const ids = new Set();
        nodes.forEach(n => {
            const vars = n.data?.config?.variables;
            if (vars) {
                Object.values(vars).forEach(b => { if (b?.device_id) ids.add(b.device_id); });
            }
            // Also alarm_banner variable_data_ids need their devices
            const alarmDetails = n.data?.config?.alarm_variable_details;
            if (alarmDetails) {
                Object.values(alarmDetails).forEach(d => { if (d?.device_id) ids.add(d.device_id); });
            }
            // Legacy single-variable fallback
            const devId = n.data?.variable_data_detail?.device_id;
            if (devId) ids.add(devId);
        });
        return Array.from(ids);
    }, [nodes]);

    const onNodesChange = useCallback((changes) => setNodes(nds => applyNodeChanges(changes, nds)), []);
    const onEdgesChange = useCallback((changes) => setEdges(eds => applyEdgeChanges(changes, eds)), []);

    // Keep refs in sync with state
    useEffect(() => { edgesRef.current = edges; }, [edges]);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);
    useEffect(() => { simSpeedupRef.current = simSpeedup; }, [simSpeedup]);

    const { data: mqttData, getVariableById } = useMQTTMultiDevice(deviceIds);

    // Update liveValues dict for every bound signal when MQTT data arrives
    useEffect(() => {
        if (isSimulationMode) return; // simulation owns liveValues
        if (!Object.keys(mqttData).length) return;
        setNodes(prev => prev.map(node => {
            const vars = node.data?.config?.variables;
            const alarmVarIds = node.data?.config?.alarm_variables;
            if (!vars && !alarmVarIds?.length) return node;

            let changed = false;
            const newLiveValues = { ...(node.data.liveValues || {}) };

            // Standard signal bindings
            if (vars) {
                Object.entries(vars).forEach(([key, binding]) => {
                    if (!binding?.variable_data_id) return;
                    const found = getVariableById(binding.variable_data_id);
                    if (found && newLiveValues[key] !== found.value) {
                        newLiveValues[key] = found.value;
                        changed = true;
                    }
                });
            }

            // Alarm banner: build alarmLiveData from alarm_variables
            let newAlarmLiveData = node.data.alarmLiveData;
            if (alarmVarIds?.length) {
                const built = {};
                alarmVarIds.forEach(vdId => {
                    const found = getVariableById(vdId);
                    if (found) {
                        built[vdId] = {
                            variable_data_id: found.id,
                            value: found.value,
                            high_active: found.high_active,
                            medium_active: found.medium_active,
                        };
                    }
                });
                const prev = node.data.alarmLiveData || {};
                if (JSON.stringify(built) !== JSON.stringify(prev)) {
                    newAlarmLiveData = built;
                    changed = true;
                }
            }

            // Early actuator unlock: if feedback variable confirms the command
            let newIsLocked = node.data.isLocked;
            const pending = pendingActuatorsRef.current[node.id];
            if (pending && changed && newLiveValues.state != null) {
                const stateVal = parseFloat(newLiveValues.state);
                const thr = pending.threshold ?? 0.5;
                const confirmed = pending.expectedState >= 0.5 ? stateVal >= thr : stateVal < thr;
                if (confirmed) {
                    clearTimeout(pending.timeoutId);
                    delete pendingActuatorsRef.current[node.id];
                    newIsLocked = false;
                }
            }

            if (!changed && newIsLocked === node.data.isLocked) return node;
            return { ...node, data: { ...node.data, liveValues: newLiveValues, alarmLiveData: newAlarmLiveData, isLocked: newIsLocked } };
        }));
    }, [mqttData, isSimulationMode]);

    // Simulation engine: tick every 1.5s when simulation mode is on
    useEffect(() => {
        if (!isSimulationMode) {
            simStateRef.current = {};
            setSimElapsed(0);
            setNodes(prev => prev.map(n => ({ ...n, data: { ...n.data, liveValues: {} } })));
            return;
        }

        const interval = setInterval(() => {
            // Advance simulated clock: each real tick = TICK_DURATION_S * speedup simulated seconds
            setSimElapsed(prev => prev + 1.5 * simSpeedupRef.current);

            // Run simulation OUTSIDE setNodes to avoid side-effects being
            // executed more than once if React replays the updater (StrictMode / concurrent).
            const currentNodes = nodesRef.current;

            // Initialize sim states for nodes that don't have one yet
            currentNodes.forEach(node => {
                if (!simStateRef.current[node.id] && SIGNAL_CONFIG[node.type]?.length > 0) {
                    simStateRef.current[node.id] = initSimState(
                        node.type, node.data?.config || {}, node.id
                    );
                }
            });

            const newLiveValuesMap = tickSimulationAll(
                currentNodes, edgesRef.current, simStateRef.current, SIGNAL_CONFIG, simSpeedupRef.current
            );

            // Advance tick counters and apply frozen signals (side-effects on simStateRef — run once)
            currentNodes.forEach(node => {
                const newLiveValues = newLiveValuesMap[node.id];
                if (!newLiveValues) return;
                const simState = simStateRef.current[node.id];
                if (simState) {
                    simState.tick += 1;
                    // Preserve manually frozen signal values (user-toggled)
                    simState.frozenSignals.forEach(key => {
                        if (key in newLiveValues) {
                            newLiveValues[key] = node.data?.liveValues?.[key] ?? newLiveValues[key];
                        }
                    });
                }
            });

            // Pure state update — no side-effects, safe if React replays
            setNodes(prev => prev.map(node => {
                const newLiveValues = newLiveValuesMap[node.id];
                if (!newLiveValues) return node;
                return { ...node, data: { ...node.data, liveValues: newLiveValues } };
            }));
        }, 1500);

        return () => clearInterval(interval);
    }, [isSimulationMode]);

    // Animate edges: BFS cascade from source nodes (no incoming edges).
    // Passive elements (no 'state' signal) always pass flow.
    // Active machines propagate flow only when their output handle is active.
    // Falls back to simple per-edge check if no seed nodes exist.
    useEffect(() => {
        if (!nodes.length) return;
        const nodeMap = {};
        nodes.forEach(n => { nodeMap[n.id] = n; });

        const outEdgeMap = {};
        const inDegree = {};
        nodes.forEach(n => { outEdgeMap[n.id] = []; inDegree[n.id] = 0; });
        edges.forEach(e => {
            if (outEdgeMap[e.source]) outEdgeMap[e.source].push(e);
            inDegree[e.target] = (inDegree[e.target] || 0) + 1;
        });

        const seeds = nodes.filter(n => !inDegree[n.id]);

        // Fallback when diagram has no source nodes (cycle / fully connected)
        if (seeds.length === 0) {
            setEdges(prev => {
                let changed = false;
                const next = prev.map(edge => {
                    const active = isEdgeHandleActive(nodeMap[edge.source], edge.sourceHandle);
                    if (edge.animated === active) return edge;
                    changed = true;
                    return { ...edge, animated: active };
                });
                return changed ? next : prev;
            });
            return;
        }

        const nodeHasFlow = {};
        seeds.forEach(n => {
            const hasLiveData = Object.keys(n.data?.liveValues || {}).length > 0;
            const signals = SIGNAL_CONFIG[n.type] || [];
            const isPassive = !signals.some(s => s.key === 'state');
            // Passive seeds (tanks, silos) need actual live data to start flow.
            // Active seeds (pumps, motors) need their state to be on.
            nodeHasFlow[n.id] = isPassive ? hasLiveData : isEdgeHandleActive(n, null);
        });
        const queue = seeds.map(n => n.id);
        const visited = new Set(queue);

        // Containers with material are always-on flow sources regardless of upstream state.
        // They must be injected into the BFS queue directly; hasContainerMaterial only
        // helps once a node IS in the queue, not if it was never reached from seeds.
        nodes.forEach(n => {
            if (['tank', 'silo', 'hopper_screw', 'reactor'].includes(n.type)
                    && parseFloat(n.data?.liveValues?.level ?? 0) > 5
                    && !visited.has(n.id)) {
                nodeHasFlow[n.id] = true;
                visited.add(n.id);
                queue.push(n.id);
            }
            // Bucket elevator is a flow source when running — its transit queue
            // carries product even when upstream tolvas are momentarily empty.
            if (n.type === 'bucket_elevator'
                    && parseFloat(n.data?.liveValues?.state ?? 0) >= 0.5
                    && !visited.has(n.id)) {
                nodeHasFlow[n.id] = true;
                visited.add(n.id);
                queue.push(n.id);
            }
            // Chiller, boiler, water_heater are independent flow sources when running.
            // They circulate fluid regardless of upstream state.
            if (['chiller', 'boiler', 'water_heater'].includes(n.type)
                    && parseFloat(n.data?.liveValues?.state ?? 0) >= 0.5
                    && !visited.has(n.id)) {
                nodeHasFlow[n.id] = true;
                visited.add(n.id);
                queue.push(n.id);
            }
        });

        const edgeActive = {};

        // Flow color based on node type — propagated through BFS
        const FLOW_COLORS = { chiller: '#1D4ED8', boiler: '#EF4444', water_heater: '#F97316' };
        const nodeFlowColor = {};
        seeds.forEach(n => { if (FLOW_COLORS[n.type]) nodeFlowColor[n.id] = FLOW_COLORS[n.type]; });
        // Also seed containers that were injected as independent sources
        nodes.forEach(n => { if (FLOW_COLORS[n.type] && !nodeFlowColor[n.id]) nodeFlowColor[n.id] = FLOW_COLORS[n.type]; });

        const edgeFlowColor = {};

        while (queue.length > 0) {
            const nodeId = queue.shift();
            const node = nodeMap[nodeId];
            if (!node) continue;

            // If this node has a type-based color, set it; otherwise inherit from upstream
            if (FLOW_COLORS[node.type]) nodeFlowColor[nodeId] = FLOW_COLORS[node.type];

            for (const e of (outEdgeMap[nodeId] || [])) {
                const nodeSignals = SIGNAL_CONFIG[node.type] || [];
                const isPassive = !nodeSignals.some(s => s.key === 'state');
                const handleOutputting = isPassive || isEdgeHandleActive(node, e.sourceHandle);

                // Batch-machine discharge handles (ribbon_blender gate, cooler_blender outlet)
                // are independent flow sources: product drains autonomously once the gate opens,
                // regardless of whether upstream ingredient machines are currently active.
                // These edges bypass the nodeHasFlow requirement.
                const isBatchDischarge = (
                    (node.type === 'ribbon_blender'
                        && (parseInt(node.data?.config?.gate_count ?? 0) > 0)
                        && ['out', 'out1', 'out2'].includes(e.sourceHandle))
                    || (node.type === 'cooler_blender'
                        && e.sourceHandle === 'out'
                        && parseFloat(node.data?.liveValues?.outlet_state ?? 0) >= 0.5)
                );

                // Air duct from cooler to cyclone: animate based solely on is_holding signal.
                // Cooler has incoming edges so it is never a BFS seed — nodeHasFlow won't be set.
                // Bypass that requirement so the edge animates whenever the cooler is processing.
                const isAirDuct = (node.type === 'cooler_blender' && e.sourceHandle === 'air_out');

                // Containers (tanks, silos, hopper_screws) with material propagate flow even without
                // continuous upstream activity — they act as independent flow sources when non-empty.
                const hasContainerMaterial = ['tank', 'silo', 'hopper_screw', 'reactor'].includes(node.type)
                    && parseFloat(node.data?.liveValues?.level ?? 0) > 5;

                const active = !!(handleOutputting && (isBatchDischarge || isAirDuct || hasContainerMaterial || nodeHasFlow[nodeId]));
                edgeActive[e.id] = active;

                // Propagate flow color downstream
                // Air ducts: light gray (air, not liquid)
                if (active && isAirDuct) {
                    edgeFlowColor[e.id] = '#94A3B8';
                // Cooler chw_out: return water temperature gradient (cold blue → warm orange)
                } else if (active && node.type === 'cooler_blender' && e.sourceHandle === 'chw_out') {
                    const temp = parseFloat(node.data?.liveValues?.temp ?? 7);
                    const t = Math.max(0, Math.min(1, (temp - 7) / 23));
                    const r = Math.round(29 + t * (249 - 29));
                    const g = Math.round(78 + t * (115 - 78));
                    const b = Math.round(216 + t * (22 - 216));
                    edgeFlowColor[e.id] = `rgb(${r},${g},${b})`;
                // Cooler product out: always green (product, not water)
                } else if (active && node.type === 'cooler_blender' && e.sourceHandle === 'out') {
                    // No color → defaults to green (#22C55E)
                } else if (active && nodeFlowColor[nodeId]) {
                    edgeFlowColor[e.id] = nodeFlowColor[nodeId];
                    if (!nodeFlowColor[e.target]) nodeFlowColor[e.target] = nodeFlowColor[nodeId];
                }

                if (active && !nodeHasFlow[e.target]) nodeHasFlow[e.target] = true;
                if (!visited.has(e.target)) {
                    visited.add(e.target);
                    queue.push(e.target);
                }
            }
        }

        // Re-propagate: batch-discharge sources (cooler/ribbon gates) may set nodeHasFlow
        // on downstream nodes AFTER those nodes were already processed by BFS. Re-check
        // inactive edges whose source now has flow, and propagate until stable.
        let recheck = true;
        while (recheck) {
            recheck = false;
            for (const e of edges) {
                if (edgeActive[e.id]) continue;          // already active
                const node = nodeMap[e.source];
                if (!node) continue;
                const sigs = SIGNAL_CONFIG[node.type] || [];
                const passive = !sigs.some(s => s.key === 'state');
                const out = passive || isEdgeHandleActive(node, e.sourceHandle);
                if (!out) continue;
                const hasMat = ['tank', 'silo', 'hopper_screw', 'reactor'].includes(node.type)
                    && parseFloat(node.data?.liveValues?.level ?? 0) > 5;
                if (nodeHasFlow[e.source] || hasMat) {
                    edgeActive[e.id] = true;
                    if (nodeFlowColor[e.source]) {
                        edgeFlowColor[e.id] = nodeFlowColor[e.source];
                        if (!nodeFlowColor[e.target]) nodeFlowColor[e.target] = nodeFlowColor[e.source];
                    }
                    if (!nodeHasFlow[e.target]) {
                        nodeHasFlow[e.target] = true;
                        recheck = true;
                    }
                }
            }
        }

        // Mark any unvisited edges (disconnected subgraphs) as inactive
        edges.forEach(e => { if (edgeActive[e.id] === undefined) edgeActive[e.id] = false; });

        setEdges(prev => {
            let changed = false;
            const next = prev.map(edge => {
                const active = edgeActive[edge.id] ?? false;
                const color = edgeFlowColor[edge.id] || null;
                const prevColor = edge.data?.flowColor || null;
                if (edge.animated === active && prevColor === color) return edge;
                changed = true;
                return { ...edge, animated: active, data: { ...edge.data, flowColor: color } };
            });
            return changed ? next : prev;
        });
    }, [nodes]);

    const loadViews = () => {
        axiosInstance.get(`scada_view/?project_id=${project_id}`).then(res => {
            setAllViews(res.data.results || res.data);
        });
    };

    // Remap legacy handle IDs from older saved diagrams to current IDs.
    // resolvedPorts is an optional array of { id } port objects for the connected node.
    const migrateHandle = (handleId, resolvedPorts) => {
        if (!handleId) return handleId;
        // Valve bidirectional handles (left_in/left_out → left, right_in/right_out → right)
        if (handleId === 'left_in'  || handleId === 'left_out')  return 'left';
        if (handleId === 'right_in' || handleId === 'right_out') return 'right';
        // Context-aware: if the node's actual ports are known and don't include this handle,
        // try stripping trailing digits (e.g. 'in1'→'in', 'out1'→'out').
        if (resolvedPorts && resolvedPorts.length > 0 && !resolvedPorts.some(p => p.id === handleId)) {
            const stripped = handleId.replace(/\d+$/, '');
            if (stripped !== handleId && resolvedPorts.some(p => p.id === stripped)) return stripped;
        }
        return handleId;
    };

    const loadView = (viewId) => {
        // Load view (edges) and elements (nodes) in parallel so that handle migration
        // can be context-aware (needs node type to decide whether 'in1' → 'in', etc.).
        Promise.all([
            axiosInstance.get(`scada_view/${viewId}/`),
            axiosInstance.get(`scada_element/?scada_view_id=${viewId}`),
        ]).then(([viewRes, elemRes]) => {
            setView(viewRes.data);
            const elements = elemRes.data.results || elemRes.data;
            setNodes(elements.map(el => elementToNode(el, isEditMode)));
            setDeletedIds([]);

            // Resolve actual port arrays per node (handles dynamic PORT_CONFIG functions
            // like pump, bucket_elevator, ribbon_blender using each element's config/width).
            const nodePortsMap = {};
            elements.forEach(el => {
                const portDef = PORT_CONFIG[el.element_type];
                if (!portDef) return;
                const elData = { config: el.config || {}, width: el.width, height: el.height };
                nodePortsMap[String(el.id)] = typeof portDef === 'function' ? portDef(elData) : portDef;
            });

            if (Array.isArray(viewRes.data.edges)) {
                const elementIds = new Set(elements.map(el => String(el.id)));

                // Helper: check if a handle exists on a node's resolved ports.
                // If the node has no PORT_CONFIG (nodePortsMap entry), we allow any handle.
                const handleExists = (handle, nodeId) => {
                    const ports = nodePortsMap[String(nodeId)];
                    if (!ports || ports.length === 0) return true; // no port info → allow
                    return ports.some(p => p.id === handle);
                };

                const migrated = viewRes.data.edges
                    // Drop edges referencing nodes that don't exist in this view
                    .filter(e => elementIds.has(String(e.source)) && elementIds.has(String(e.target)))
                    .map(e => ({
                        ...e,
                        sourceHandle: migrateHandle(e.sourceHandle, nodePortsMap[String(e.source)]),
                        targetHandle: migrateHandle(e.targetHandle, nodePortsMap[String(e.target)]),
                        type: 'deletable',
                        data: { ...e.data, editMode: isEditMode },
                    }))
                    // Drop edges whose handles don't exist on the node (stale after migration)
                    .filter(e => handleExists(e.sourceHandle, e.source) && handleExists(e.targetHandle, e.target));

                // Deduplicate after handle migration (migration can collapse different
                // handle names to the same ID, producing duplicate ReactFlow keys).
                const seen = new Set();
                const deduped = migrated.filter(e => {
                    const k = `${e.source}_${e.sourceHandle}_${e.target}_${e.targetHandle}`;
                    if (seen.has(k)) return false;
                    seen.add(k);
                    return true;
                });
                setEdges(deduped);
            }
        });
    };

    useEffect(() => {
        setCompanyChange(prev => prev + 1);
        loadViews();
    }, [project_id]);

    useEffect(() => {
        if (id) loadView(id);
    }, [id]);

    // Propagate edit mode to node data and edge data
    useEffect(() => {
        setNodes(prev => prev.map(n => ({
            ...n,
            data: { ...n.data, isEditMode },
            draggable: isEditMode,
            selectable: isEditMode,
            connectable: isEditMode,
        })));
        setEdges(prev => prev.map(e => ({
            ...e,
            type: 'deletable',
            data: { ...e.data, editMode: isEditMode },
        })));
    }, [isEditMode]);

    const onConnect = useCallback(params => setEdges(eds => addEdge({ ...params, type: 'deletable', data: { editMode: isEditMode } }, eds)), [isEditMode]);

    const onDrop = useCallback((event) => {
        event.preventDefault();
        if (!reactFlowInstance || !isEditMode) return;
        const type = event.dataTransfer.getData('application/scada-element-type');
        console.log('[onDrop] type:', type);
        if (!type) return;
        const defW = parseInt(event.dataTransfer.getData('application/scada-element-w') || '80');
        const defH = parseInt(event.dataTransfer.getData('application/scada-element-h') || '80');
        const machineTypeId = event.dataTransfer.getData('application/scada-machine-type-id');

        const bounds = reactFlowWrapper.current.getBoundingClientRect();
        const position = reactFlowInstance.project({
            x: event.clientX - bounds.left,
            y: event.clientY - bounds.top,
        });

        const tempId = newTempId();
        let machine_type_detail = null;
        if (machineTypeId) {
            // Load machine type detail async and update node
            axiosInstance.get(`scada_machine_type/${machineTypeId}/`).then(res => {
                setNodes(prev => prev.map(n =>
                    n.id === tempId ? { ...n, data: { ...n.data, machine_type_id: parseInt(machineTypeId), machine_type_detail: res.data } } : n
                ));
            });
        }

        const newNode = {
            id: tempId,
            type,
            position,
            data: {
                element_type: type,
                width: defW,
                height: defH,
                liveValue: null,
                isEditMode: true,
                variable_data_id: null,
                variable_data_detail: null,
                machine_type_id: machineTypeId ? parseInt(machineTypeId) : null,
                machine_type_detail,
                config: {},
                label: '',
                rotation: 0,
            },
            style: { width: defW, height: defH },
            draggable: true,
            selectable: true,
            connectable: true,
        };
        setNodes(prev => {
            console.log('[onDrop] adding node, prev.length:', prev.length, '→', prev.length + 1, '| tempId:', tempId, '| type:', type);
            return [...prev, newNode];
        });
    }, [reactFlowInstance, isEditMode]);

    const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

    const onNodeClick = useCallback((_, node) => {
        if (isEditMode) {
            setSelectedNode(node);
            return;
        }
        // Simulation mode: open gate panel for blenders, toggle state for all others
        if (isSimulationMode) {
            const signals = SIGNAL_CONFIG[node.type] || [];
            const hasGates = ['ribbon_blender', 'cooler_blender'].includes(node.type)
                && signals.some(s => ['gate_1', 'outlet_state'].includes(s.key));
            if (hasGates) {
                setGatePanel(node.id);
                return;
            }
            const simState = simStateRef.current[node.id];
            if (simState && signals.some(s => s.key === 'state')) {
                const currentState = parseFloat(node.data?.liveValues?.state ?? 0);
                const newStateVal = currentState >= 0.5 ? 0 : 1;
                simState.frozenSignals.add('state');
                simState.currentBoolValues.state = newStateVal;
                setNodes(prev => prev.map(n => n.id !== node.id ? n
                    : { ...n, data: { ...n.data, liveValues: { ...n.data.liveValues, state: newStateVal } } }
                ));
            }
            return;
        }
        // View mode: send actuator command if configured
        const config = node.data?.config;
        if (!config?.is_actuator) return;

        // Ignore click if command already pending (lock active)
        if (pendingActuatorsRef.current[node.id]) return;

        const actuatorDeviceId = config.actuator_device_id
            ?? node.data?.variable_data_detail?.device_id
            ?? null;
        if (!actuatorDeviceId) return;

        // valve_3way: open position picker panel
        if (node.type === 'valve_3way') {
            setActuatorPanel({ type: 'valve_3way', node });
            return;
        }

        // valve_ball: open slider panel
        if (node.type === 'valve_ball') {
            const currentPos = parseFloat(node.data?.liveValues?.position ?? 50);
            setBallSliderValue(isNaN(currentPos) ? 50 : Math.round(currentPos));
            setActuatorPanel({ type: 'valve_ball', node });
            return;
        }

        // valve_gate / valve_butterfly / led_indicator: toggle open/close buttons
        const threshold = parseFloat(config.threshold ?? 0.5);
        const currentVal = parseFloat(node.data?.liveValues?.state ?? node.data?.liveValue ?? 0);
        const isOpen = currentVal >= threshold;
        const buttonBinding = isOpen ? config.actuator_close : config.actuator_open;
        if (!buttonBinding?.variable_id) return;

        const expectedState = isOpen ? 0 : 1;
        const lockDuration = parseInt(config.toggle_lock_duration ?? 5) * 1000;

        axiosInstance.post('device_action_button/', {
            device_id: actuatorDeviceId,
            variable_id: buttonBinding.variable_id,
            value: String(expectedState),
        }).catch(() => {});

        // Lock the element until feedback arrives or timeout
        const timeoutId = setTimeout(() => {
            delete pendingActuatorsRef.current[node.id];
            setNodes(prev => prev.map(n => n.id !== node.id ? n
                : { ...n, data: { ...n.data, isLocked: false } }
            ));
        }, lockDuration);

        pendingActuatorsRef.current[node.id] = { expectedState, timeoutId, threshold };
        setNodes(prev => prev.map(n => n.id !== node.id ? n
            : { ...n, data: { ...n.data, isLocked: true } }
        ));
    }, [isEditMode, isSimulationMode]);

    const onPaneClick = () => { setSelectedNode(null); setSelectedEdge(null); };

    const onEdgeClick = useCallback((_, edge) => {
        if (isEditMode) {
            setSelectedEdge(edge);
            setSelectedNode(null);
        }
    }, [isEditMode]);


    const onNodeDataChange = (nodeId, partialData) => {
        setNodes(prev => prev.map(n => {
            if (n.id !== nodeId) return n;
            const newData = { ...n.data, ...partialData };
            const newStyle = { ...n.style };
            if (partialData.width) newStyle.width = partialData.width;
            if (partialData.height) newStyle.height = partialData.height;
            return { ...n, data: newData, style: newStyle };
        }));
        // Update selectedNode reference
        setSelectedNode(prev => prev && prev.id === nodeId ? { ...prev, data: { ...prev.data, ...partialData } } : prev);
    };

    const onDeleteNode = (nodeId) => {
        const numericId = parseInt(nodeId);
        if (numericId > 0) {
            setDeletedIds(prev => [...prev, numericId]);
        }
        setNodes(prev => prev.filter(n => n.id !== nodeId));
        setSelectedNode(null);
    };

    const save = async () => {
        if (!id) return;
        setSaving(true);
        try {
            console.log('[save] nodes at save time:', nodes.length, nodes.map(n => ({id: n.id, type: n.type})));
            const payloads = nodes.map(n => nodeToPayload(n, parseInt(id)));
            const res = await axiosInstance.put('scada_element/bulk_save/', {
                scada_view_id: parseInt(id),
                elements: payloads,
                deleted_ids: deletedIds,
                edges,
            });
            setDeletedIds([]);

            // Backend returns id_map: { tempId: realId, ... }
            const idMap = res.data?.id_map || {};
            console.log('[SCADA save] bulk_save OK — id_map:', idMap, '| payloads sent:', payloads.map(p => ({id: p.id, type: p.element_type})));

            // Reload fresh nodes with real IDs, update edges with remapped IDs
            const elRes = await axiosInstance.get(`scada_element/?scada_view_id=${id}`);
            const freshList = elRes.data.results || elRes.data;
            console.log('[SCADA save] fresh GET →', elRes.data);
            if (Array.isArray(freshList) && freshList.length > 0) {
                const freshNodes = freshList.map(el => elementToNode(el, isEditMode));
                const remappedEdges = edges.map(e => ({
                    ...e,
                    source: idMap[e.source] ?? e.source,
                    target: idMap[e.target] ?? e.target,
                }));
                setNodes(freshNodes);
                setEdges(remappedEdges);
            }
            setSavedMsg(true);
            setTimeout(() => setSavedMsg(false), 2000);
        } catch (err) {
            const msg = err?.response?.data
                ? JSON.stringify(err.response.data).slice(0, 200)
                : (err?.message || 'Error desconocido');
            setSaveError(msg);
            setTimeout(() => setSaveError(null), 6000);
        } finally {
            setSaving(false);
        }
    };

    const createScreen = () => {
        axiosInstance.post('scada_view/', { name: newScreenForm.name, project: project_id }).then(res => {
            setShowNewScreen(false);
            setNewScreenForm({ name: '' });
            loadViews();
            navigate(`/scada/${res.data.id}`);
        });
    };

    if (showMachineTypes) {
        return (
            <div className={styles.editorWrapper}>
                <div className={styles.toolbar}>
                    <button className={styles.secondaryBtn} onClick={() => setShowMachineTypes(false)}>
                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> {t('scada.back')}
                    </button>
                    <div className={styles.toolbarTitle}>{t('scada.myMachines')}</div>
                </div>
                <div style={{ flex: 1, overflow: 'auto', background: '#0F172A' }}>
                    <SCADAMachineTypes projectId={project_id} onClose={() => setShowMachineTypes(false)} />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.editorWrapper}>
            {/* ─── Toolbar ─── */}
            <div className={styles.toolbar}>
                <button className={styles.secondaryBtn} style={{ padding: '5px 10px' }} onClick={() => navigate('/scada')}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <div className={styles.toolbarTitle}>{view?.name || '...'}</div>

                {/* Screen tabs */}
                <div style={{ display: 'flex', gap: 4, overflow: 'hidden', flex: 1 }}>
                    {allViews.map(v => (
                        <div
                            key={v.id}
                            className={`${styles.tab} ${String(v.id) === id ? styles.tabActive : ''}`}
                            onClick={() => navigate(`/scada/${v.id}`)}
                        >
                            {v.name}
                        </div>
                    ))}
                    <div className={`${styles.tab} ${styles.tabAddBtn}`} onClick={() => setShowNewScreen(true)}>
                        <FontAwesomeIcon icon={faPlus} />
                    </div>
                </div>

                {/* Mode toggle */}
                <div className={styles.modeToggle}>
                    <button className={`${styles.modeBtn} ${!isEditMode ? styles.modeBtnActive : ''}`} onClick={() => setIsEditMode(false)}>
                        {t('scada.viewMode')}
                    </button>
                    <button className={`${styles.modeBtn} ${isEditMode ? styles.modeBtnActive : ''}`} onClick={() => setIsEditMode(true)}>
                        {t('scada.editMode')}
                    </button>
                </div>

                {/* Simulate button + speedup selector */}
                <button
                    onClick={() => setIsSimulationMode(v => !v)}
                    style={{
                        background: isSimulationMode ? '#D97706' : 'transparent',
                        color: isSimulationMode ? '#FEF3C7' : '#94A3B8',
                        border: `1px solid ${isSimulationMode ? '#F59E0B' : '#334155'}`,
                        borderRadius: 8, padding: '5px 12px', fontSize: 12,
                        fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                    }}
                >
                    {isSimulationMode ? t('scada.simulating') : t('scada.simulate')}
                </button>
                {isSimulationMode && (() => {
                    // Format simulated elapsed time as Xd Xh Xm
                    const totalMin = Math.floor(simElapsed / 60);
                    const days  = Math.floor(totalMin / 1440);
                    const hours = Math.floor((totalMin % 1440) / 60);
                    const mins  = totalMin % 60;
                    const label = days > 0
                        ? `${days}d ${hours}h ${mins}m`
                        : hours > 0
                            ? `${hours}h ${mins}m`
                            : `${mins}m`;
                    return (
                        <>
                            <div style={{ fontSize: 11, color: '#FEF3C7', background: '#451A03',
                                border: '1px solid #F59E0B', borderRadius: 6, padding: '4px 8px',
                                flexShrink: 0, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                ⏱ {label}
                            </div>
                            <select
                                value={simSpeedup}
                                onChange={e => setSimSpeedup(Number(e.target.value))}
                                style={{ background: '#1E293B', color: '#FEF3C7', border: '1px solid #F59E0B',
                                    borderRadius: 6, padding: '4px 8px', fontSize: 12, flexShrink: 0 }}
                            >
                                <option value={1}>{t('scada.speedReal')}</option>
                                <option value={10}>{t('scada.speedFactor', { n: 10 })}</option>
                                <option value={60}>{t('scada.speedFactor', { n: 60 })}</option>
                                <option value={300}>{t('scada.speedFactor', { n: 300 })}</option>
                                <option value={3600}>{t('scada.speedFactor', { n: 3600 })}</option>
                            </select>
                        </>
                    );
                })()}

                {/* Save button */}
                {isEditMode && (
                    <>
                        <button className={styles.primaryBtn} onClick={save} disabled={saving} style={{ flexShrink: 0 }}>
                            {saving
                                ? <FontAwesomeIcon icon={faSpinner} spin />
                                : savedMsg
                                    ? t('scada.saved')
                                    : <><FontAwesomeIcon icon={faSave} /> {t('scada.save')}</>
                            }
                        </button>
                        {saveError && (
                            <div style={{ background: '#7F1D1D', border: '1px solid #EF4444', borderRadius: 4,
                                padding: '4px 10px', fontSize: 11, color: '#FEF2F2', maxWidth: 320,
                                whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {t('scada.saveError')}: {saveError}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ─── Editor body ─── */}
            <div className={styles.editorBody}>
                {/* Left palette (edit mode only) */}
                {isEditMode && (
                    <SCADAElementPalette
                        projectId={project_id}
                        onShowMachineTypes={() => setShowMachineTypes(true)}
                    />
                )}

                {/* Canvas */}
                <div className={styles.canvasArea} ref={reactFlowWrapper}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={isEditMode ? onNodesChange : undefined}
                        onEdgesChange={isEditMode ? onEdgesChange : undefined}
                        onConnect={isEditMode ? onConnect : undefined}
                        onInit={setReactFlowInstance}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onNodeClick={onNodeClick}
                        onNodeMouseEnter={!isEditMode ? (_, n) => {
                            if (hoverLeaveTimerRef.current) clearTimeout(hoverLeaveTimerRef.current);
                            setHoveredNode(n);
                        } : undefined}
                        onNodeMouseLeave={!isEditMode ? () => {
                            hoverLeaveTimerRef.current = setTimeout(() => setHoveredNode(null), 250);
                        } : undefined}
                        onEdgeClick={isEditMode ? onEdgeClick : undefined}
                        onPaneClick={onPaneClick}
                        nodeTypes={scadaNodeTypes}
                        edgeTypes={scadaEdgeTypes}
                        fitView
                        fitViewOptions={{ padding: 0.1 }}
                        minZoom={0.1}
                        maxZoom={3}
                        snapToGrid={isEditMode}
                        snapGrid={[10, 10]}
                        deleteKeyCode={isEditMode ? 'Delete' : null}
                        connectionMode="loose"
                        connectionLineType="smoothstep"
                        connectionLineStyle={{ stroke: '#94A3B8', strokeWidth: 2 }}
                        defaultEdgeOptions={{ style: { stroke: '#94A3B8', strokeWidth: 2 }, type: 'deletable' }}
                        style={{ background: '#0F172A' }}
                    >
                        {isEditMode && <Background color="#1E293B" gap={20} size={1} />}
                        <Controls style={{ bottom: 16, right: 16, left: 'unset' }} showInteractive={false} />
                    </ReactFlow>

                    {/* Live-values hover panel — view mode only */}
                    {!isEditMode && hoveredNode && (() => {
                        // Look up the current node from the nodes array so liveValues refresh each tick.
                        const currentNode = nodes.find(n => n.id === hoveredNode.id) || hoveredNode;
                        const lv = currentNode.data?.liveValues || {};
                        const signals = SIGNAL_CONFIG[hoveredNode.type] || [];
                        const cfg = hoveredNode.data?.config || {};
                        const threshold = parseFloat(cfg.threshold ?? 0.5);
                        const hasError = parseFloat(lv.error) >= threshold;
                        const label = hoveredNode.data?.label || hoveredNode.type;

                        if (signals.length === 0 && !hoveredNode.data?.isLocked) return null;

                        return (
                            <div
                                onMouseEnter={() => {
                                    if (hoverLeaveTimerRef.current) clearTimeout(hoverLeaveTimerRef.current);
                                }}
                                onMouseLeave={() => {
                                    hoverLeaveTimerRef.current = setTimeout(() => setHoveredNode(null), 150);
                                }}
                                style={{
                                    position: 'absolute', bottom: 56, left: 12, zIndex: 50,
                                    background: '#0F172A', border: `1px solid ${hasError ? '#EF4444' : '#334155'}`,
                                    borderRadius: 8, padding: '10px 12px', minWidth: 180, maxWidth: 260,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                    pointerEvents: 'all',
                                }}>
                                <div style={{
                                    fontSize: 11, fontWeight: 700, color: '#F1F5F9',
                                    marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6,
                                }}>
                                    {hasError && <span style={{ color: '#EF4444' }}>⚠</span>}
                                    {label}
                                    {hoveredNode.data?.isLocked && (
                                        <span style={{ fontSize: 9, color: '#F59E0B', background: '#451A03',
                                            border: '1px solid #F59E0B', borderRadius: 3, padding: '1px 4px' }}>
                                            {t('scada.pending')}
                                        </span>
                                    )}
                                </div>
                                {signals.map(sig => {
                                    const raw = lv[sig.key];
                                    if (raw === undefined || raw === null) return null;
                                    const isErr = sig.key === 'error' || sig.key === 'fault';
                                    const isBool = sig.dataType === 'boolean';
                                    const num = parseFloat(raw);
                                    const isActive = isBool && !isNaN(num) && num >= threshold;
                                    const isWarn = isErr && !isNaN(num) && num >= threshold;
                                    // For count signal: show units and optional kg if weight configured
                                    let displayVal;
                                    if (sig.key === 'count' && !isNaN(num)) {
                                        const weightG = parseFloat(cfg.sim?.sim_unit_weight_g ?? 0);
                                        const kg = weightG > 0 ? ` (${(num * weightG / 1000).toFixed(1)} kg)` : '';
                                        displayVal = `${num.toLocaleString()} u${kg}`;
                                    } else {
                                        displayVal = isBool
                                            ? (isActive ? t('scada.signalOn') : t('scada.signalOff'))
                                            : (isNaN(num) ? String(raw) : (num % 1 === 0 ? num : num.toFixed(2)));
                                    }
                                    return (
                                        <div key={sig.key} style={{
                                            display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'center', gap: 8,
                                            fontSize: 10, padding: '2px 0',
                                            color: isWarn ? '#EF4444' : '#94A3B8',
                                        }}>
                                            <span>{t(sig.label)}</span>
                                            <span style={{
                                                fontWeight: 600, fontFamily: 'monospace',
                                                color: isWarn ? '#EF4444' : isBool
                                                    ? (isActive ? '#22C55E' : '#64748B')
                                                    : '#F1F5F9',
                                            }}>{displayVal}</span>
                                        </div>
                                    );
                                })}
                                {/* Elapsed runtime — shown in simulation mode for any running machine */}
                                {isSimulationMode && lv.runtime_s > 0 && (() => {
                                    const s = Math.round(parseFloat(lv.runtime_s));
                                    const totalMin = Math.floor(s / 60);
                                    const sec = s % 60;
                                    const h = Math.floor(totalMin / 60);
                                    const min = totalMin % 60;
                                    const label = h > 0
                                        ? `${h}h ${min}m`
                                        : totalMin > 0
                                            ? `${totalMin}m ${sec}s`
                                            : `${sec}s`;
                                    return (
                                        <div style={{
                                            display: 'flex', justifyContent: 'space-between',
                                            alignItems: 'center', gap: 8,
                                            fontSize: 10, padding: '2px 0',
                                            color: '#94A3B8', borderTop: '1px solid #1E293B', marginTop: 4, paddingTop: 6,
                                        }}>
                                            <span>{t('scada.sig.runtimeS')}</span>
                                            <span style={{ fontWeight: 600, fontFamily: 'monospace', color: '#38BDF8' }}>{label}</span>
                                        </div>
                                    );
                                })()}
                                {/* Elevator request queue — shown in simulation mode */}
                                {isSimulationMode && hoveredNode.type === 'bucket_elevator' && (() => {
                                    const queue = lv._requestQueue || [];
                                    const dischargeCfg = hoveredNode.data?.config?.sim?.sim_discharge_order || 'fifo';
                                    if (queue.length === 0 && !nodes.some(n =>
                                        n.id !== hoveredNode.id &&
                                        ['packager_single','packager_multi','packager_doypack','tank','silo','hopper_screw'].includes(n.type)
                                    )) return null;

                                    // Collect downstream nodes excluding return edges
                                    const downstreamNodes = edges
                                        .filter(e => e.source === hoveredNode.id && e.sourceHandle !== 'return')
                                        .map(e => nodes.find(n => n.id === e.target))
                                        .filter(Boolean);
                                    if (downstreamNodes.length === 0) return null;

                                    const elevRunning = parseFloat(lv.state ?? 0) >= 0.5;

                                    return (
                                        <div style={{ borderTop: '1px solid #1E3A5F', marginTop: 8, paddingTop: 8 }}>
                                            {/* Header */}
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <span style={{ fontSize: 10, fontWeight: 700, color: '#93C5FD', letterSpacing: 0.5 }}>
                                                    📋 {t('elevatorDischargeQueue')}
                                                </span>
                                                <span style={{
                                                    fontSize: 9, fontWeight: 700, padding: '1px 6px',
                                                    borderRadius: 10, letterSpacing: 0.5,
                                                    background: dischargeCfg === 'lifo' ? '#3B1F6B' : '#0C2340',
                                                    color: dischargeCfg === 'lifo' ? '#C4B5FD' : '#60A5FA',
                                                    border: `1px solid ${dischargeCfg === 'lifo' ? '#7C3AED' : '#1D4ED8'}`,
                                                }}>
                                                    {dischargeCfg.toUpperCase()}
                                                </span>
                                            </div>

                                            {/* Queue entries — sorted by queue position */}
                                            {[...downstreamNodes].sort((a, b) => {
                                                const posA = queue.indexOf(a.id);
                                                const posB = queue.indexOf(b.id);
                                                // Queued items first (by position), non-queued last
                                                if (posA === -1 && posB === -1) return 0;
                                                if (posA === -1) return 1;
                                                if (posB === -1) return -1;
                                                return posA - posB;
                                            }).map((n) => {
                                                const nlv  = n.data?.liveValues || {};
                                                const ncfg = n.data?.config?.sim || {};
                                                const isPackager = ['packager_single','packager_multi','packager_doypack'].includes(n.type);
                                                const simSt = simStateRef.current[n.id];

                                                // Buffer / level %
                                                let bufPct = null;
                                                if (isPackager && simSt?.packagerBufferKg != null) {
                                                    const cap = parseFloat(ncfg.sim_capacity_kg ?? 200);
                                                    bufPct = Math.round(simSt.packagerBufferKg / cap * 100);
                                                } else if (!isPackager && simSt?.levelKg != null) {
                                                    const cap = parseFloat(ncfg.sim_capacity_kg ?? 1000);
                                                    bufPct = Math.round(simSt.levelKg / cap * 100);
                                                } else if (nlv.level != null) {
                                                    bufPct = Math.round(parseFloat(nlv.level));
                                                }

                                                const queuePos  = queue.indexOf(n.id);
                                                const waiting   = queuePos !== -1;
                                                const isNext    = queue[0] === n.id && elevRunning;
                                                const label     = n.data?.label || n.type;

                                                // Bar color
                                                const barColor  = bufPct == null ? '#334155'
                                                    : bufPct < 20 ? '#EF4444'
                                                    : bufPct < 50 ? '#F59E0B'
                                                    : '#22C55E';

                                                return (
                                                    <div key={n.id} style={{
                                                        display: 'flex', alignItems: 'center', gap: 6,
                                                        padding: '4px 6px', marginBottom: 3, borderRadius: 6,
                                                        background: isNext
                                                            ? 'rgba(34,197,94,0.12)'
                                                            : waiting
                                                            ? 'rgba(59,130,246,0.08)'
                                                            : 'transparent',
                                                        border: isNext
                                                            ? '1px solid rgba(34,197,94,0.3)'
                                                            : waiting
                                                            ? '1px solid rgba(59,130,246,0.15)'
                                                            : '1px solid transparent',
                                                    }}>
                                                        {/* Position badge */}
                                                        <div style={{
                                                            width: 18, height: 18, borderRadius: '50%',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: 9, fontWeight: 700, flexShrink: 0,
                                                            background: isNext ? '#14532D' : waiting ? '#1E3A5F' : '#1E293B',
                                                            color: isNext ? '#4ADE80' : waiting ? '#60A5FA' : '#475569',
                                                        }}>
                                                            {waiting ? queuePos + 1 : '—'}
                                                        </div>

                                                        {/* Name */}
                                                        <span style={{
                                                            flex: 1, fontSize: 10, fontWeight: waiting ? 600 : 400,
                                                            color: isNext ? '#4ADE80' : waiting ? '#E2E8F0' : '#64748B',
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {label}
                                                        </span>

                                                        {/* Buffer bar + % */}
                                                        {bufPct != null && (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                                                <div style={{ width: 40, height: 5, background: '#1E293B', borderRadius: 3, overflow: 'hidden' }}>
                                                                    <div style={{ width: `${bufPct}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width 0.4s' }} />
                                                                </div>
                                                                <span style={{ fontSize: 9, color: barColor, fontWeight: 700, minWidth: 24, textAlign: 'right' }}>
                                                                    {bufPct}%
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Time since last request (real seconds) */}
                                                        {(() => {
                                                            const lastReqTimes = lv._lastRequestTime || {};
                                                            const ts = lastReqTimes[n.id];
                                                            if (!ts) return null;
                                                            const elapsedS = Math.round((Date.now() - ts) / 1000);
                                                            return (
                                                                <span style={{
                                                                    fontSize: 9, color: '#94A3B8', fontFamily: 'monospace',
                                                                    flexShrink: 0, minWidth: 28, textAlign: 'right',
                                                                }}>
                                                                    {elapsedS}s
                                                                </span>
                                                            );
                                                        })()}

                                                        {/* "siguiente" chip */}
                                                        {isNext && (
                                                            <span style={{
                                                                fontSize: 8, fontWeight: 700, padding: '1px 5px',
                                                                borderRadius: 8, background: '#166534', color: '#4ADE80',
                                                                flexShrink: 0,
                                                            }}>
                                                                ▶
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {queue.length === 0 && (
                                                <div style={{ fontSize: 10, color: '#475569', textAlign: 'center', padding: '4px 0', fontStyle: 'italic' }}>
                                                    {t('elevatorNoPending')}
                                                </div>
                                            )}

                                            {/* Return targets section */}
                                            {(() => {
                                                const returnTargets = lv._returnTargets || [];
                                                if (returnTargets.length === 0) return null;
                                                const returnKg = lv._returnKgThisTick || 0;
                                                return (
                                                    <div style={{ borderTop: '1px solid #1E3A5F', marginTop: 8, paddingTop: 8 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: '#A78BFA', letterSpacing: 0.5 }}>
                                                                🔄 {t('elevatorReturn')}
                                                            </span>
                                                            {returnKg > 0 && (
                                                                <span style={{
                                                                    fontSize: 9, fontWeight: 700, padding: '1px 6px',
                                                                    borderRadius: 10, background: '#3B1F6B', color: '#C4B5FD',
                                                                    border: '1px solid #7C3AED',
                                                                }}>
                                                                    +{returnKg.toFixed(1)} kg/tick
                                                                </span>
                                                            )}
                                                        </div>
                                                        {returnTargets.map((rt, idx) => {
                                                            const barColor = rt.containerPct < 20 ? '#EF4444'
                                                                : rt.containerPct < 50 ? '#F59E0B' : '#22C55E';
                                                            return (
                                                                <div key={idx} style={{
                                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                                    padding: '4px 6px', marginBottom: 3, borderRadius: 6,
                                                                    background: returnKg > 0 ? 'rgba(167,139,250,0.08)' : 'transparent',
                                                                    border: returnKg > 0 ? '1px solid rgba(167,139,250,0.2)' : '1px solid transparent',
                                                                }}>
                                                                    <div style={{
                                                                        width: 18, height: 18, borderRadius: '50%',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        fontSize: 9, flexShrink: 0, background: '#2D1B4E', color: '#A78BFA',
                                                                    }}>
                                                                        ↩
                                                                    </div>
                                                                    <span style={{
                                                                        flex: 1, fontSize: 10, fontWeight: 500, color: '#CBD5E1',
                                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                    }}>
                                                                        {rt.passthroughLabel}
                                                                        {rt.containerLabel && (
                                                                            <span style={{ color: '#64748B', fontSize: 9 }}> → {rt.containerLabel}</span>
                                                                        )}
                                                                    </span>
                                                                    {rt.containerId && (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                                                            <div style={{ width: 40, height: 5, background: '#1E293B', borderRadius: 3, overflow: 'hidden' }}>
                                                                                <div style={{ width: `${rt.containerPct}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width 0.4s' }} />
                                                                            </div>
                                                                            <span style={{ fontSize: 9, color: barColor, fontWeight: 700, minWidth: 24, textAlign: 'right' }}>
                                                                                {rt.containerPct}%
                                                                            </span>
                                                                            <span style={{ fontSize: 8, color: '#64748B', minWidth: 40, textAlign: 'right' }}>
                                                                                {rt.containerLevelKg}/{rt.containerCapKg}kg
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })()}

                                {/* Simulation controls inline in hover panel */}
                                {isSimulationMode && (() => {
                                    const simState = simStateRef.current[hoveredNode.id];
                                    if (!simState) return null;
                                    const hasState = signals.some(s => s.key === 'state');
                                    const hasError = signals.some(s => s.key === 'error');
                                    const isBlender = ['ribbon_blender', 'cooler_blender'].includes(hoveredNode.type);
                                    if (!hasState && !hasError) return null;
                                    const stateOn = parseFloat(lv.state ?? 0) >= threshold;
                                    const alarmOn = !!simState.manualError;
                                    const smallBtn = (active, danger, onClick, children) => (
                                        <button
                                            onClick={onClick}
                                            style={{
                                                pointerEvents: 'all', cursor: 'pointer',
                                                flex: 1, border: 'none', borderRadius: 6,
                                                padding: '4px 6px', fontSize: 10, fontWeight: 600,
                                                background: danger
                                                    ? (active ? '#7F1D1D' : '#EF4444')
                                                    : (active ? '#14532D' : '#374151'),
                                                color: '#fff',
                                            }}
                                        >{children}</button>
                                    );
                                    return (
                                        <div style={{ borderTop: '1px solid #334155', marginTop: 6, paddingTop: 6,
                                            display: 'flex', gap: 6, pointerEvents: 'all' }}>
                                            {hasState && !isBlender && smallBtn(stateOn, false, () => {
                                                const newVal = stateOn ? 0 : 1;
                                                simState.frozenSignals.add('state');
                                                simState.currentBoolValues.state = newVal;
                                                setNodes(prev => prev.map(n => n.id !== hoveredNode.id ? n
                                                    : { ...n, data: { ...n.data, liveValues: { ...n.data.liveValues, state: newVal } } }
                                                ));
                                            }, stateOn ? t('scada.simStop') : t('scada.simStart'))}
                                            {hasState && isBlender && smallBtn(false, false, () => setGatePanel(hoveredNode.id), t('scada.simControl'))}
                                            {hasError && smallBtn(alarmOn, true, () => {
                                                simState.manualError = !simState.manualError;
                                                setNodes(prev => prev.map(n => n.id !== hoveredNode.id ? n
                                                    : { ...n, data: { ...n.data, liveValues: { ...n.data.liveValues, error: simState.manualError ? 1 : 0 } } }
                                                ));
                                            }, alarmOn ? t('scada.alarmClear') : t('scada.alarmTrigger'))}
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })()}
                </div>

                {/* Right inspector (edit mode only) */}
                {isEditMode && !selectedEdge && (
                    <SCADAElementInspector
                        node={selectedNode}
                        projectId={project_id}
                        allNodes={nodes}
                        onChange={onNodeDataChange}
                        onDelete={onDeleteNode}
                    />
                )}

                {/* Edge properties panel (edit mode, edge selected) */}
                {isEditMode && selectedEdge && (() => {
                    const edgeData = edges.find(e => e.id === selectedEdge.id)?.data || {};
                    const updateEdgeData = (partial) => {
                        setEdges(eds => eds.map(e => {
                            if (e.id !== selectedEdge.id) return e;
                            return { ...e, data: { ...e.data, ...partial } };
                        }));
                        setSelectedEdge(prev => prev ? { ...prev, data: { ...prev.data, ...partial } } : prev);
                    };
                    return (
                        <div style={{
                            width: 260, background: '#1E293B', borderLeft: '1px solid #334155',
                            padding: 16, overflowY: 'auto', flexShrink: 0,
                        }}>
                            <h3 style={{ color: '#F1F5F9', fontSize: 14, margin: '0 0 16px', fontWeight: 600 }}>
                                {t('scada.edgeProperties', 'Propiedades de conexión')}
                            </h3>

                            {/* Pipe style toggle */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ color: '#94A3B8', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={!!edgeData.pipeStyle}
                                        onChange={(e) => updateEdgeData({ pipeStyle: e.target.checked })}
                                        style={{ accentColor: '#4F46E5' }}
                                    />
                                    {t('scada.pipeStyle', 'Estilo tubería')}
                                </label>
                            </div>

                            {/* Diameter input (only when pipe style is on) */}
                            {edgeData.pipeStyle && (
                                <div style={{ marginBottom: 14 }}>
                                    <label style={{ color: '#94A3B8', fontSize: 12, display: 'block', marginBottom: 4 }}>
                                        {t('scada.pipeDiameter', 'Diámetro (mm)')}
                                    </label>
                                    <input
                                        type="number"
                                        min={10}
                                        max={500}
                                        step={5}
                                        value={edgeData.diameter || 50}
                                        onChange={(e) => updateEdgeData({ diameter: parseFloat(e.target.value) || 50 })}
                                        style={{
                                            width: '100%', padding: '6px 8px', borderRadius: 6,
                                            border: '1px solid #334155', background: '#0F172A',
                                            color: '#F1F5F9', fontSize: 13, boxSizing: 'border-box',
                                        }}
                                    />
                                </div>
                            )}

                            {/* Waypoints info */}
                            <div style={{ marginBottom: 14 }}>
                                <div style={{ color: '#94A3B8', fontSize: 12, marginBottom: 4 }}>
                                    Waypoints: {(edgeData.waypoints || []).length}
                                    {(edgeData.waypoints || []).length > 0 && (
                                        <button
                                            onClick={() => updateEdgeData({ waypoints: [] })}
                                            style={{
                                                marginLeft: 8, background: 'none', border: '1px solid #475569',
                                                color: '#94A3B8', borderRadius: 4, padding: '2px 6px',
                                                fontSize: 11, cursor: 'pointer',
                                            }}
                                        >
                                            {t('scada.clearWaypoints', 'Limpiar')}
                                        </button>
                                    )}
                                </div>
                                <div style={{ color: '#64748B', fontSize: 11, lineHeight: 1.4 }}>
                                    {t('scada.waypointHint', 'Doble clic en la línea para agregar puntos. Clic derecho en un punto para eliminarlo.')}
                                </div>
                            </div>

                            {/* Delete edge button */}
                            <button
                                onClick={() => {
                                    setEdges(eds => eds.filter(e => e.id !== selectedEdge.id));
                                    setSelectedEdge(null);
                                }}
                                style={{
                                    width: '100%', padding: '8px 0', borderRadius: 6,
                                    border: 'none', background: '#EF4444', color: '#fff',
                                    fontSize: 13, cursor: 'pointer', fontWeight: 500,
                                }}
                            >
                                {t('scada.deleteEdge', 'Eliminar conexión')}
                            </button>
                        </div>
                    );
                })()}
            </div>

            {/* New screen modal */}
            <Modal show={showNewScreen} setter={setShowNewScreen} name={t('scada.newScreen')} onClick={createScreen} buttonName={t('common.save')}>
                <TextInput name="name" placeholder={t('scada.screenName')} form={newScreenForm} setForm={setNewScreenForm} />
            </Modal>

            {/* Actuator panel overlay (valve_3way position picker / valve_ball slider) */}
            {actuatorPanel && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.5)',
                }} onClick={() => setActuatorPanel(null)}>
                    <div style={{
                        background: '#1E293B', border: '1px solid #334155', borderRadius: 12,
                        padding: 20, minWidth: 220, maxWidth: 320,
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ color: '#F1F5F9', fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
                            {actuatorPanel.node.data?.label || actuatorPanel.node.type}
                        </div>

                        {/* valve_3way: position buttons */}
                        {actuatorPanel.type === 'valve_3way' && (() => {
                            const cfg = actuatorPanel.node.data?.config || {};
                            const deviceId = cfg.actuator_device_id
                                ?? actuatorPanel.node.data?.variable_data_detail?.device_id;
                            const lockDuration = parseInt(cfg.toggle_lock_duration ?? 5) * 1000;
                            const sendPos = (binding) => {
                                if (!binding?.variable_id || !deviceId) return;
                                axiosInstance.post('device_action_button/', {
                                    device_id: deviceId,
                                    variable_id: binding.variable_id,
                                    value: '1',
                                }).catch(() => {});
                                const nodeId = actuatorPanel.node.id;
                                const timeoutId = setTimeout(() => {
                                    delete pendingActuatorsRef.current[nodeId];
                                    setNodes(prev => prev.map(n => n.id !== nodeId ? n
                                        : { ...n, data: { ...n.data, isLocked: false } }));
                                }, lockDuration);
                                pendingActuatorsRef.current[nodeId] = { expectedState: 1, timeoutId, threshold: 0.5 };
                                setNodes(prev => prev.map(n => n.id !== actuatorPanel.node.id ? n
                                    : { ...n, data: { ...n.data, isLocked: true } }));
                                setActuatorPanel(null);
                            };
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>{t('scada.valvePosition')}</div>
                                    {[
                                        { key: 'actuator_pos_a', label: t('scada.valvePosA') },
                                        { key: 'actuator_pos_b', label: t('scada.valvePosB') },
                                        { key: 'actuator_pos_c', label: t('scada.valvePosC') },
                                    ].map(({ key, label }) => cfg[key]?.variable_id ? (
                                        <button key={key} onClick={() => sendPos(cfg[key])} style={{
                                            background: '#0F172A', color: '#F1F5F9', border: '1px solid #475569',
                                            borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                                            textAlign: 'left',
                                        }}>{label}</button>
                                    ) : null)}
                                </div>
                            );
                        })()}

                        {/* valve_ball: slider */}
                        {actuatorPanel.type === 'valve_ball' && (() => {
                            const cfg = actuatorPanel.node.data?.config || {};
                            const deviceId = cfg.actuator_device_id
                                ?? actuatorPanel.node.data?.variable_data_detail?.device_id;
                            const lockDuration = parseInt(cfg.toggle_lock_duration ?? 5) * 1000;
                            const sendSlider = () => {
                                const binding = cfg.actuator_slider;
                                if (!binding?.variable_id || !deviceId) return;
                                axiosInstance.post('device_action_button/', {
                                    device_id: deviceId,
                                    variable_id: binding.variable_id,
                                    value: String(ballSliderValue),
                                }).catch(() => {});
                                const nodeId = actuatorPanel.node.id;
                                const timeoutId = setTimeout(() => {
                                    delete pendingActuatorsRef.current[nodeId];
                                    setNodes(prev => prev.map(n => n.id !== nodeId ? n
                                        : { ...n, data: { ...n.data, isLocked: false } }));
                                }, lockDuration);
                                pendingActuatorsRef.current[nodeId] = { expectedState: ballSliderValue, timeoutId, threshold: 0.5 };
                                setNodes(prev => prev.map(n => n.id !== actuatorPanel.node.id ? n
                                    : { ...n, data: { ...n.data, isLocked: true } }));
                                setActuatorPanel(null);
                            };
                            return (
                                <div>
                                    <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 8 }}>{t('scada.valveSlider')}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                        <input type="range" min={0} max={100} step={1}
                                            value={ballSliderValue}
                                            onChange={e => setBallSliderValue(Number(e.target.value))}
                                            style={{ flex: 1 }} />
                                        <span style={{ color: '#F1F5F9', fontSize: 14, minWidth: 36, textAlign: 'right' }}>
                                            {ballSliderValue}%
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button onClick={sendSlider} style={{
                                            flex: 1, background: '#4F46E5', color: '#fff',
                                            border: 'none', borderRadius: 8, padding: '8px 0',
                                            fontSize: 13, cursor: 'pointer', fontWeight: 600,
                                        }}>{t('common.send')}</button>
                                        <button onClick={() => setActuatorPanel(null)} style={{
                                            flex: 1, background: 'transparent', color: '#94A3B8',
                                            border: '1px solid #334155', borderRadius: 8, padding: '8px 0',
                                            fontSize: 13, cursor: 'pointer',
                                        }}>{t('common.cancel')}</button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Gate / state control panel (simulation mode — ribbon_blender & cooler_blender) */}
            {gatePanel && isSimulationMode && (() => {
                // Always read from live nodes so buttons reflect current state immediately
                const node = nodes.find(n => n.id === gatePanel);
                if (!node) return null;
                const simState = simStateRef.current[node.id];
                const cfg = node.data?.config || {};
                const lv  = node.data?.liveValues || {};
                const isRibbon = node.type === 'ribbon_blender';
                const gateCount = parseInt(cfg.gate_count ?? 1);
                const label = node.data?.label || node.type;

                const stateOn = parseFloat(lv.state ?? 0) >= parseFloat(cfg.threshold ?? 0.5);

                const toggleState = () => {
                    if (!simState) return;
                    const newVal = stateOn ? 0 : 1;
                    simState.frozenSignals.add('state');
                    simState.currentBoolValues.state = newVal;
                    setNodes(prev => prev.map(n => n.id !== node.id ? n
                        : { ...n, data: { ...n.data, liveValues: { ...n.data.liveValues, state: newVal } } }
                    ));
                };

                const toggleGate = (gateKey, stateKey) => {
                    if (!simState) return;
                    const curVal = parseFloat(lv[gateKey] ?? 0);
                    const newVal = curVal >= 0.5 ? 0 : 1;
                    simState.frozenSignals.add(gateKey);
                    if (stateKey === 'gateState')  simState.gateState  = newVal;
                    if (stateKey === 'gate2State') simState.gate2State = newVal;
                    if (stateKey === 'outletState') simState.outletState = newVal;
                    setNodes(prev => prev.map(n => n.id !== node.id ? n
                        : { ...n, data: { ...n.data, liveValues: { ...n.data.liveValues, [gateKey]: newVal } } }
                    ));
                };

                const toggleAlarm = () => {
                    if (!simState) return;
                    simState.manualError = !simState.manualError;
                    setNodes(prev => prev.map(n => n.id !== node.id ? n
                        : { ...n, data: { ...n.data, liveValues: { ...n.data.liveValues, error: simState.manualError ? 1 : 0 } } }
                    ));
                };

                const btnStyle = (active, danger) => ({
                    flex: 1, borderRadius: 8, padding: '9px 0',
                    fontSize: 13, cursor: 'pointer', fontWeight: 600,
                    transition: 'all 0.12s',
                    outline: 'none',
                    background: danger
                        ? (active ? '#450a0a' : '#DC2626')
                        : (active ? '#15803d' : '#374151'),
                    color: '#fff',
                    border: active
                        ? `2px solid ${danger ? '#EF4444' : '#4ade80'}`
                        : '2px solid transparent',
                    boxShadow: active
                        ? `0 0 0 2px ${danger ? 'rgba(239,68,68,0.3)' : 'rgba(74,222,128,0.3)'}`
                        : 'none',
                });

                return (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.5)',
                    }} onClick={() => setGatePanel(null)}>
                        <div style={{
                            background: '#1E293B', border: '1px solid #334155', borderRadius: 12,
                            padding: 20, minWidth: 240, maxWidth: 300,
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ color: '#F1F5F9', fontWeight: 700, fontSize: 14, marginBottom: 14 }}>
                                {label}
                            </div>

                            {/* ON / OFF */}
                            <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 4 }}>{t('scada.machine')}</div>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                <button onClick={toggleState} style={btnStyle(stateOn, false)}>
                                    {stateOn ? t('scada.simStop') : t('scada.simStart')}
                                </button>
                            </div>

                            {/* Gates */}
                            {isRibbon ? (
                                <>
                                    <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 4 }}>{t('scada.gates')}</div>
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                        <button onClick={() => toggleGate('gate_1', 'gateState')}
                                            style={btnStyle(parseFloat(lv.gate_1 ?? 0) >= 0.5, false)}>
                                            {t('scada.gate1')} {parseFloat(lv.gate_1 ?? 0) >= 0.5 ? '●' : '○'}
                                        </button>
                                        {gateCount >= 2 && (
                                            <button onClick={() => toggleGate('gate_2', 'gate2State')}
                                                style={btnStyle(parseFloat(lv.gate_2 ?? 0) >= 0.5, false)}>
                                                {t('scada.gate2')} {parseFloat(lv.gate_2 ?? 0) >= 0.5 ? '●' : '○'}
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 4 }}>{t('scada.outlet')}</div>
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                        <button onClick={() => toggleGate('outlet_state', 'outletState')}
                                            style={btnStyle(parseFloat(lv.outlet_state ?? 0) >= 0.5, false)}>
                                            {t('scada.outlet')} {parseFloat(lv.outlet_state ?? 0) >= 0.5 ? '●' : '○'}
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Alarm */}
                            <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 4 }}>{t('scada.alarm')}</div>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                                <button onClick={toggleAlarm}
                                    style={btnStyle(false, true)}>
                                    {simState?.manualError ? t('scada.alarmClear') : t('scada.alarmTrigger')}
                                </button>
                            </div>

                            <button onClick={() => setGatePanel(null)} style={{
                                width: '100%', background: 'transparent', color: '#94A3B8',
                                border: '1px solid #334155', borderRadius: 8, padding: '7px 0',
                                fontSize: 12, cursor: 'pointer',
                            }}>{t('common.close')}</button>
                        </div>
                    </div>
                );
            })()}

        </div>
    );
}
